import { type FC, useMemo, useState } from "react";
import { Stack } from "@mantine/core";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import "dayjs/locale/nb";
import "dayjs/locale/en-gb";
import Modal from "@/components/molecules/Modal";
import ModalHeader from "@/components/atoms/ModalHeader";
import Text from "@/components/atoms/Text";
import Tooltip from "@/components/atoms/Tooltip";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { ScheduleViewLevel } from "@mantine/schedule";
import { useGetFractionWindows, useGetReceptionCalendarSlots } from "@/services/ReceptionSettings";
import { useGetAllSalesItemsByLanguageId } from "@/services/Product";
import { useAuth } from "@/contexts/AuthContext";
import { classNames } from "@/utils/Classnames";
import { resolveActiveFractionWindow, resolveAcceptedFractionNames } from "@/utils/ReceptionFractionWindows";
import {
  buildMonthDays,
  buildWeekDays,
  getVisibleTimeRows,
  groupSlotsByDate,
  isSlotFull,
  isSlotPartial,
  startOfWeek,
} from "@/utils/ReceptionSlotGrid";
import styles from "@/components/molecules/ReceptionSlotGrid/styles.module.css";
import localStyles from "./ReceptionCalendarModal.module.css";

dayjs.extend(isoWeek);

interface Props {
  opened: boolean;
  onClose: () => void;
  receiverCompanyId: number;
}

/** Beyond this many accepted fractions, show a count instead of listing every name inline. */
const MAX_INLINE_FRACTION_NAMES = 3;

const ReceptionCalendarModal: FC<Props> = ({ opened, onClose, receiverCompanyId }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const dayjsLocale = i18n.language.startsWith("no") ? "nb" : "en-gb";

  const [monthDate, setMonthDate] = useState(() => dayjs());
  const [weekStart, setWeekStart] = useState(() => startOfWeek(dayjs()));
  const [view, setView] = useState<ScheduleViewLevel>("week");

  const weekFromDate = weekStart.format("YYYY-MM-DD");
  const weekToDate = weekStart.add(6, "day").format("YYYY-MM-DD");
  const monthWeeks = useMemo(() => buildMonthDays(monthDate), [monthDate]);
  const monthFromDate = monthWeeks[0][0].iso;
  const monthToDate = monthWeeks[monthWeeks.length - 1][6].iso;

  const fromDate = view === "week" ? weekFromDate : monthFromDate;
  const toDate = view === "week" ? weekToDate : monthToDate;

  const { data: slots = [] } = useGetReceptionCalendarSlots(receiverCompanyId, fromDate, toDate, null, {
    enabled: opened && !!receiverCompanyId,
  });

  const { data: fractionWindows = [] } = useGetFractionWindows(receiverCompanyId, {
    enabled: opened && !!receiverCompanyId,
  });

  const { data: salesItems = [] } = useGetAllSalesItemsByLanguageId(user?.language?.id ?? 1, { enabled: opened });

  const fractionNameMap = useMemo(
    () => new Map(salesItems.map((item) => [item.salesItemId, item.salesItemTranslated])),
    [salesItems]
  );

  interface FractionLabel {
    /** Short label for the card itself — truncated to a count once past MAX_INLINE_FRACTION_NAMES. */
    display: string;
    /** Full accepted-fraction list, always spelled out, for the hover tooltip. */
    full: string;
  }

  const resolveFractionLabel = useMemo(() => {
    const cache = new Map<string, FractionLabel>();
    return (iso: string): FractionLabel => {
      const cached = cache.get(iso);
      if (cached) return cached;
      const activeWindow = resolveActiveFractionWindow(iso, fractionWindows);
      const acceptedNames = activeWindow ? resolveAcceptedFractionNames(activeWindow, fractionNameMap) : [];
      const result: FractionLabel = !activeWindow
        ? { display: "—", full: "—" }
        : activeWindow.acceptAll
          ? { display: t("orders:deliverySlotPicker.allFractions"), full: t("orders:deliverySlotPicker.allFractions") }
          : acceptedNames.length === 0
            ? { display: "—", full: "—" }
            : {
                display:
                  acceptedNames.length > MAX_INLINE_FRACTION_NAMES
                    ? t("settings:reception.fractionWindows.fractions", { count: acceptedNames.length })
                    : acceptedNames.join(" "),
                full: acceptedNames.join(", "),
              };
      cache.set(iso, result);
      return result;
    };
  }, [fractionWindows, fractionNameMap, t]);

  const days = useMemo(() => buildWeekDays(weekStart, dayjsLocale), [weekStart, dayjsLocale]);
  const slotsByDate = useMemo(() => groupSlotsByDate(slots), [slots]);
  const timeRows = useMemo(() => getVisibleTimeRows(slots), [slots]);

  const isPrevWeekDisabled = weekStart.isSame(startOfWeek(dayjs()), "day");

  const localizedWeekStart = weekStart.locale(dayjsLocale);
  const localizedWeekEnd = localizedWeekStart.add(6, "day");
  const weekLabel = `${t("common:week")} ${localizedWeekStart.isoWeek()} · ${
    localizedWeekStart.month() === localizedWeekEnd.month()
      ? `${localizedWeekStart.format("D")}–${localizedWeekEnd.format("D MMM YYYY")}`
      : `${localizedWeekStart.format("D MMM")}–${localizedWeekEnd.format("D MMM YYYY")}`
  }`;

  const monthLabel = monthDate.locale(dayjsLocale).format("MMMM YYYY");
  const monthWeekdayLabels = buildWeekDays(startOfWeek(monthDate), dayjsLocale).map((d) => d.weekdayLabel);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="1400px"
      title={<ModalHeader title={t("settings:reception.calendar.title")} svgAsset="schedule" />}
    >
      <Stack gap="md" py="20px">
        <div className={classNames(styles.navRow, styles.navRowPadded)}>
          {view === "week" ? (
            <>
              <div className={styles.navBtnGroup}>
                <button
                  type="button"
                  className={styles.navBtn}
                  aria-label={t("containers:schedule.prevWeek")}
                  disabled={isPrevWeekDisabled}
                  onClick={() => !isPrevWeekDisabled && setWeekStart((w) => w.subtract(7, "day"))}
                >
                  <SVGAsset name="chevron-right" width={12} height={12} style={{ transform: "rotate(180deg)" }} />
                </button>
                <button
                  type="button"
                  className={styles.navBtn}
                  aria-label={t("containers:schedule.nextWeek")}
                  onClick={() => setWeekStart((w) => w.add(7, "day"))}
                >
                  <SVGAsset name="chevron-right" width={12} height={12} />
                </button>
              </div>
              <Text size="sm" strong display="technical" className={styles.weekLabel}>
                {weekLabel}
              </Text>
            </>
          ) : (
            <>
              <div className={styles.navBtnGroup}>
                <button
                  type="button"
                  className={styles.navBtn}
                  aria-label={t("containers:schedule.prevWeek")}
                  onClick={() => setMonthDate((d) => d.subtract(1, "month"))}
                >
                  <SVGAsset name="chevron-right" width={12} height={12} style={{ transform: "rotate(180deg)" }} />
                </button>
                <button
                  type="button"
                  className={styles.navBtn}
                  aria-label={t("containers:schedule.nextWeek")}
                  onClick={() => setMonthDate((d) => d.add(1, "month"))}
                >
                  <SVGAsset name="chevron-right" width={12} height={12} />
                </button>
              </div>
              <Text size="sm" strong display="technical" className={styles.weekLabel}>
                {monthLabel}
              </Text>
            </>
          )}

          <div className={styles.legend}>
            <span className={styles.legendItem}>
              <span className={classNames(styles.legendSwatch, styles.legendSwatchOpen)} />
              <Text size="xs" color="secondary" display="technical" tt="uppercase">
                {t("settings:reception.calendar.open")}
              </Text>
            </span>
            <span className={styles.legendItem}>
              <span className={classNames(styles.legendSwatch, styles.legendSwatchPartial)} />
              <Text size="xs" color="secondary" display="technical" tt="uppercase">
                {t("settings:reception.calendar.partial")}
              </Text>
            </span>
            <span className={styles.legendItem}>
              <span className={classNames(styles.legendSwatch, styles.legendSwatchFull)} />
              <Text size="xs" color="secondary" display="technical" tt="uppercase">
                {t("settings:reception.calendar.full")}
              </Text>
            </span>
            <span className={styles.legendItem}>
              <span className={classNames(styles.legendSwatch, styles.legendSwatchClosed)} />
              <Text size="xs" color="secondary" display="technical" tt="uppercase">
                {t("settings:reception.calendar.closed")}
              </Text>
            </span>
          </div>

          <div className={localStyles.viewToggle}>
            <button
              type="button"
              className={classNames(localStyles.viewToggleBtn, view === "week" ? localStyles.viewToggleBtnActive : "")}
              onClick={() => setView("week")}
            >
              {t("common:week")}
            </button>
            <button
              type="button"
              className={classNames(localStyles.viewToggleBtn, view === "month" ? localStyles.viewToggleBtnActive : "")}
              onClick={() => setView("month")}
            >
              {t("common:month")}
            </button>
          </div>
        </div>

        {view === "week" ? (
          <>
            <div
              className={styles.grid}
              style={{ gridTemplateColumns: `64px repeat(${days.length}, minmax(104px, 1fr))` }}
            >
              <div className={styles.dayHeadRow}>
                <div className={styles.timeAxisHead} />
                {days.map((day) => (
                  <div key={day.iso} className={classNames(styles.dayHead, day.isToday ? styles.dayHeadToday : "")}>
                    <Text size="xs" display="technical" tt="uppercase" color="secondary" className={styles.dayWeekday}>
                      {day.weekdayLabel}
                    </Text>
                    <Text size="md" strong className={styles.dayNumber}>
                      {day.dayNumber}
                    </Text>
                    <Text size="xs" display="technical" tt="uppercase" color="secondary" className={styles.dayStatus}>
                      {day.isPast
                        ? t("orders:receptionSlots.legend.past")
                        : day.isToday
                          ? t("orders:deliverySlotPicker.today")
                          : t("orders:deliverySlotPicker.slotCount", {
                              count: (slotsByDate.get(day.iso) ?? []).length,
                            })}
                    </Text>
                  </div>
                ))}
              </div>

              {timeRows.map((time) => (
                <div key={time} className={styles.dayHeadRow}>
                  <div className={styles.timeAxisCell} data-testid="time-axis-label">
                    <Text size="xs" display="technical" color="secondary">
                      {time.slice(0, 5)}
                    </Text>
                  </div>
                  {days.map((day) => {
                    const slot = (slotsByDate.get(day.iso) ?? []).find((s) => s.startTime === time);
                    if (!slot) {
                      return (
                        <div
                          key={day.iso}
                          data-testid="day-cell-closed"
                          className={classNames(styles.dayCell, styles.dayCellClosed)}
                        />
                      );
                    }
                    const full = isSlotFull(slot);
                    const partial = !full && isSlotPartial(slot);
                    const stateClass = full
                      ? styles.slotCardFull
                      : partial
                        ? styles.slotCardPartial
                        : styles.slotCardOpen;
                    const fractionLabel = resolveFractionLabel(day.iso);
                    return (
                      <div key={day.iso} className={styles.dayCell}>
                        <div className={classNames(styles.slotCard, stateClass)}>
                          <span className={styles.slotCardTime}>{slot.startTime.slice(0, 5)}</span>
                          <Tooltip label={fractionLabel.full} disabled={fractionLabel.full === fractionLabel.display}>
                            <span className={styles.slotCardFraction}>{fractionLabel.display}</span>
                          </Tooltip>
                          <span className={styles.slotCardStatus}>
                            {t("settings:reception.calendar.slotFreeCount", {
                              count: slot.remainingCapacity,
                              max: slot.capacity,
                            })}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            {timeRows.length === 0 && (
              <Text size="sm" color="secondary" ta="center" className={styles.emptyState}>
                {t("settings:reception.calendar.empty")}
              </Text>
            )}
          </>
        ) : (
          <div className={localStyles.monthGrid}>
            {monthWeekdayLabels.map((label) => (
              <Text
                key={label}
                size="xs"
                display="technical"
                tt="uppercase"
                color="secondary"
                className={localStyles.monthWeekdayHead}
              >
                {label}
              </Text>
            ))}

            {monthWeeks.flat().map((day) => {
              const daySlots = slotsByDate.get(day.iso) ?? [];
              const hasSlots = daySlots.length > 0;
              const allFull = hasSlots && daySlots.every(isSlotFull);
              const openCount = daySlots.filter((slot) => !isSlotFull(slot)).length;
              const fractionLabel = resolveFractionLabel(day.iso);

              return (
                <div
                  key={day.iso}
                  className={classNames(
                    localStyles.monthDayCard,
                    !day.isCurrentMonth ? localStyles.monthDayCardOtherMonth : "",
                    day.isToday ? localStyles.monthDayCardToday : "",
                    !hasSlots
                      ? localStyles.monthDayCardEmpty
                      : allFull
                        ? localStyles.monthDayCardFull
                        : localStyles.monthDayCardOpen
                  )}
                >
                  <span className={localStyles.monthDayNumber}>{day.dayNumber}</span>
                  {hasSlots && (
                    <>
                      <Tooltip label={fractionLabel.full} disabled={fractionLabel.full === fractionLabel.display}>
                        <span className={localStyles.monthDayFraction}>{fractionLabel.display}</span>
                      </Tooltip>
                      <span className={localStyles.monthDayCount}>
                        {t("settings:reception.calendar.slotsOpenCount", {
                          open: openCount,
                          total: daySlots.length,
                        })}
                      </span>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Stack>
    </Modal>
  );
};

export default ReceptionCalendarModal;
